"use client";

import { useApolloClient } from "@apollo/client/react";
import { use, useCallback, useEffect, useState } from "react";

import { EditorContextProvider } from "@/components/app/editor-v3/editor-context";
import { CommentEditor } from "@/components/app/editor-v3/editors/comment-editor";
import { toPostRequestParams } from "@/components/app/editor-v3/transforms/to-post-request-params";
import { CommentThreadContextProvider } from "@/components/app/post/post-comments/comment-thread-context";
import { EmptyState } from "@/components/shared/empty-state";
import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { toast } from "@/components/ui/use-toast";
import { PostContextProvider, usePostContext } from "@/contexts/post-context";
import { UserContext } from "@/contexts/user-context";
import type { Post } from "@/graphql/codegen/graphql";
import { useIntersectionObserver } from "@/hooks/use-intersection-observer";
import { useSubmitPost } from "@/hooks/use-submit-post";
import { isAbortError } from "@/utils/helpers";
import { cn } from "@/utils/lib";

import { CommentSort } from "./comment-sort";
import { useCommentsContext } from "./comments-context";
import { PostCommentItem } from "./post-comment-item";
import { PostCommentSkeletonList } from "./post-comment-skeleton-list";

const getAllPostHashInThread = (comments: Post[], acc: string[] = []) => {
  for (const comment of comments) {
    acc.push(comment.postHash);
    if (comment?.replies?.nodes) {
      getAllPostHashInThread(comment.replies.nodes as Post[], acc);
    }
  }
  return acc;
};

const filterUniqueComments = (comments: Post[]) => {
  const seen = new Set();
  return comments.filter((comment) => {
    if (seen.has(comment.postHash)) {
      return false;
    }
    seen.add(comment.postHash);
    return true;
  });
};

export const PostComments = () => {
  const { user, currentUserPublicKey } = use(UserContext);
  const {
    post,
    username,
    publicKey: postCreatorPublicKey,
    isPureRepost,
  } = usePostContext();
  const apolloClient = useApolloClient();

  const postHash = isPureRepost ? post.repostedPostHash : post.postHash;

  if (!postCreatorPublicKey) {
    throw new Error("postCreatorPublicKey is missing");
  }

  if (!postHash) {
    throw new Error("postHash is missing");
  }

  const {
    data,
    loading,
    loadMoreRepliesForComment,
    onCommentCreated,
    OPCommentsData,
    reset,
    setOrderByPreset,
    orderByPreset,
    updateComment,
    paginationState,
  } = useCommentsContext();
  const hasMoreComments = paginationState[postHash]?.hasMore;

  const submitComment = useSubmitPost();

  const OPComments =
    (OPCommentsData?.postByPostHash?.replies.nodes.filter(Boolean) as Post[]) ||
    [];
  const comments =
    (data?.postByPostHash?.replies.nodes.filter(Boolean) as Post[]) || [];
  const allComments = filterUniqueComments(OPComments.concat(comments));

  const totalCount = data?.postByPostHash?.replies.totalCount ?? 0;
  const totalCommentsCount = totalCount + OPComments.length;
  const commentsLabel =
    totalCommentsCount > 0 ? `Comments (${totalCommentsCount})` : "Comments";
  const [loadingMore, setLoadingMore] = useState(false);

  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0.5,
    rootMargin: "1000px",
  });

  const loadMoreComments = useCallback(async () => {
    if (hasMoreComments && !loadingMore) {
      setLoadingMore(true);
      try {
        await loadMoreRepliesForComment(postHash);
      } catch (error) {
        if (!isAbortError(error)) {
          console.error(error);
        }
      } finally {
        setLoadingMore(false);
      }
    }
  }, [hasMoreComments, loadingMore, loadMoreRepliesForComment, postHash]);

  useEffect(() => {
    if (isIntersecting) {
      loadMoreComments();
    }
  }, [isIntersecting, loadMoreComments]);

  return (
    <>
      <div className="border-b border-border-light p-4">
        <EditorContextProvider
          enableFileDrop
          onSubmit={async (editorContext) => {
            const { setEditorContext, reset, exposed } = editorContext;
            const setModalOpen = exposed?.setModalOpen;

            if (typeof setModalOpen === "function") {
              setModalOpen(false);
            }

            setEditorContext({
              submitting: true,
            });
            const requestData = await toPostRequestParams({
              editorContext,
              user: user!,
              initialData: {
                UpdaterPublicKeyBase58Check: currentUserPublicKey,
                ParentStakeID: postHash,
              },
            });
            try {
              const comment = await submitComment(requestData, {
                toleratePostHydrationFailure: true,
              });

              if (comment) {
                onCommentCreated(comment as Post);
              }
              reset();
            } catch (error) {
              toast({
                title: "Error submitting comment",
                description: `An error happened when submitting your comment`,
                details: createToastErrorDetails(error),
                variant: "destructive",
              });
            } finally {
              setEditorContext({
                submitting: false,
              });
            }
          }}
        >
          <CommentEditor to={username} />
        </EditorContextProvider>
      </div>
      <div
        className="
          unforce-radius flex items-center justify-between border-b
          border-border-light p-0 px-4 py-2 text-xs text-muted
        "
      >
        <p>{commentsLabel}</p>
        <CommentSort
          orderByPreset={orderByPreset}
          onOrderChange={(value) => {
            setOrderByPreset(value);
            reset();
          }}
        />
      </div>
      <div>
        <div
          className={cn("unforce-radius w-full", {
            "border-b border-border-light":
              loading || loadingMore || allComments.length > 0,
          })}
        >
          {loading ? (
            <PostCommentSkeletonList count={4} />
          ) : allComments.length === 0 ? (
            <div className="flex items-center justify-center p-12">
              <EmptyState
                image="/assets/empty-23.png"
                title="No comments yet"
                description="Be the first to leave a comment."
              />
            </div>
          ) : (
            allComments.map((comment, i) => (
              <PostContextProvider
                key={comment.postHash}
                updatePostByHash={(hash, post) => {
                  const newPost =
                    typeof post === "function" ? post(comment) : post;
                  updateComment(hash, newPost);

                  try {
                    apolloClient.cache.modify({
                      id: apolloClient.cache.identify(newPost),
                      // @ts-expect-error - This is a workaround for a bug in Apollo Client
                      fields: newPost,
                    });
                  } catch {
                    /* we don't care if it throws error */
                  }
                }}
                {...{
                  post: comment,
                }}
              >
                <CommentThreadContextProvider
                  postHashes={[
                    comment.postHash,
                    ...getAllPostHashInThread(
                      (comment?.replies?.nodes || []) as Post[],
                    ),
                  ]}
                >
                  <PostCommentItem
                    {...{
                      postCreatorPublicKey,
                      loadMoreRepliesForComment,
                      nestingLevel: 1,
                      isLastItem: i === allComments.length - 1,
                      isFirstItem: i === 0,
                    }}
                  />
                </CommentThreadContextProvider>
              </PostContextProvider>
            ))
          )}
        </div>
        <div ref={ref} className="h-px w-full" />
        {loadingMore && <PostCommentSkeletonList count={2} />}
      </div>
    </>
  );
};
